import {
  AccountLanguage,
  CompanyName,
  Country,
  Department,
  JobTitle,
  Manager,
  OfficeLocation,
} from '@/client/accounts';
import { Branch } from '@/client/branches';
import { Group } from '@/client/groups';
import { FilterNamesEnum } from '@/client/helpers/filter.helper';
import { Tag } from '@/client/tags';
import {
  BranchesMultiselectInput,
  BranchesSelectInput,
  CompanyNamesMultiselectInput,
  CountriesMultiselectInput,
  GroupsMultiselectInput,
  GroupsSelectInput,
  JobTitlesMultiselectInput,
  ManagersEmailMultiselectInput,
  OfficeLocationsMultiselectInput,
} from '@/components/form/selectors';
import { DepartmentsMultiselectInput } from '@/components/form/selectors/DepartmentsMultiselectInput';
import { ManagersMultiselectInput } from '@/components/form/selectors/ManagersMultiselectInput';
import { PhishingLanguagesMultiselectInput } from '@/components/form/selectors/PhishingLanguagesMultiselectInput';
import { TagsMultiselectInput } from '@/components/form/selectors/TagsMultiselectInput';
import { UserLanguagesMultiselectInput } from '@/components/form/selectors/UserLanguagesMultiselectInput';
import { FilterType, FilterTypeEnum } from '@/components/tables/crud/types';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { preventDropdownBrowserTranslate } from '@/utils/helpers/ui.helper';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { DataTableBetweenFilter } from './DataTableBetweenFilter';
import { DataTableInputFilter } from './DataTableInputFilter';

type FilterValue =
  | string
  | object
  | number
  | undefined
  | { from?: string | undefined; to?: string | undefined }
  | string[]
  | number[];

type FilterProps = {
  filter: FilterType;
  value: FilterValue;
  onChange: (value: FilterValue, field: string) => void;
  onRemove: (field: string) => void;
};

export const DataTableFilter: React.FC<FilterProps> = ({
  filter,
  value,
  onChange,
  onRemove,
}) => {
  const { t } = useTranslation();
  const optionLabels: { [k: string]: { yes: string; no: string } } = {
    [FilterTypeEnum.SELECT_YES_NO]: {
      yes: t('dialog.yes'),
      no: t('dialog.no'),
    },
    [FilterTypeEnum.SELECT_AUTO_MANUAL]: {
      yes: t('generic.auto'),
      no: t('generic.manual'),
    },
    [FilterTypeEnum.SELECT_ACTIVE_INACTIVE]: {
      yes: t('generic.active'),
      no: t('generic.inactive'),
    },
    [FilterTypeEnum.SELECT_PUBLISHED_DRAFT]: {
      yes: t('generic.published'),
      no: t('generic.draft'),
    },
    [FilterTypeEnum.SELECT_UPCOMING_PAST]: {
      yes: t('filter.date.upcoming'),
      no: t('filter.date.past'),
    },
    [FilterTypeEnum.SELECT_AVAILABLE_UNAVAILABLE]: {
      yes: t('generic.available'),
      no: t('generic.unavailable'),
    },
  };
  return (
    <FlexContainer wrap="nowrap" width="auto">
      <div className="p-inputgroup">
        <span className="p-inputgroup-addon">{filter.label}</span>
        {filter.type === FilterTypeEnum.TEXT && (
          <DataTableInputFilter
            {...filter}
            onChange={onChange}
            value={value as string | undefined}
            placeholder={filter.placeholder as string}
          />
        )}
        {filter.type === FilterTypeEnum.SELECT && (
          <>
            {[
              FilterNamesEnum.BRANCH,
              FilterNamesEnum.USER_BRANCH,
              FilterNamesEnum.AWARENESS_REPORTS_BRANCH,
            ].includes(filter.field) ? (
              <BranchesSelectInput
                onChange={(branch) => onChange(branch, filter.field)}
                placeholder={filter.placeholder as string}
                selectedValue={value as Branch}
              />
            ) : [FilterNamesEnum.GROUPS, FilterNamesEnum.USER_GROUPS].includes(
                filter.field,
              ) ? (
              <GroupsSelectInput
                onChange={(group) => onChange(group, filter.field)}
                placeholder={filter.placeholder as string}
                selectedValue={value as Group}
              />
            ) : (
              <Dropdown
                options={filter.options}
                placeholder={filter.placeholder as string}
                value={value}
                onChange={(event) => onChange(event.value, filter.field)}
              />
            )}
          </>
        )}
        {[
          FilterTypeEnum.SELECT_YES_NO,
          FilterTypeEnum.SELECT_AUTO_MANUAL,
          FilterTypeEnum.SELECT_ACTIVE_INACTIVE,
          FilterTypeEnum.SELECT_PUBLISHED_DRAFT,
          FilterTypeEnum.SELECT_UPCOMING_PAST,
          FilterTypeEnum.SELECT_AVAILABLE_UNAVAILABLE,
        ].includes(filter.type) && (
          <Dropdown
            options={[
              {
                label: optionLabels[filter.type].yes,
                value: 'yes',
              },
              {
                label: optionLabels[filter.type].no,
                value: 'no',
              },
            ]}
            placeholder={filter.placeholder as string}
            value={value}
            onChange={(event) => onChange(event.value, filter.field)}
            {...preventDropdownBrowserTranslate}
          />
        )}
        {filter.type === FilterTypeEnum.BETWEEN && (
          <DataTableBetweenFilter
            {...filter}
            placeholder={filter.placeholder as string[]}
            value={
              value as { from?: string | undefined; to?: string | undefined }
            }
            onChange={(event) => onChange(event.value, filter.field)}
          />
        )}
        {filter.type === FilterTypeEnum.MULTI_SELECT && (
          <>
            {[
              FilterNamesEnum.BRANCH,
              FilterNamesEnum.USER_BRANCH,
              FilterNamesEnum.AWARENESS_REPORTS_BRANCH,
            ].includes(filter.field) ? (
              <BranchesMultiselectInput
                onChange={(branches) => onChange(branches, filter.field)}
                selectedOptions={value as Branch[]}
                placeholder={filter.placeholder as string}
              />
            ) : [FilterNamesEnum.GROUPS, FilterNamesEnum.USER_GROUPS].includes(
                filter.field,
              ) ? (
              <GroupsMultiselectInput
                selectedOptions={value as Group[]}
                onChange={(groups) => onChange(groups, filter.field)}
                placeholder={filter.placeholder as string}
              />
            ) : [
                FilterNamesEnum.DEPARTMENT,
                FilterNamesEnum.USER_DEPARTMENT,
              ].includes(filter.field) ? (
              <DepartmentsMultiselectInput
                selectedOptions={value as Department[]}
                onChange={(departments) => onChange(departments, filter.field)}
              />
            ) : [
                FilterNamesEnum.MANAGER,
                FilterNamesEnum.USER_MANAGER,
              ].includes(filter.field) ? (
              <ManagersMultiselectInput
                selectedOptions={value as Manager[]}
                onChange={(managers) => onChange(managers, filter.field)}
              />
            ) : [
                FilterNamesEnum.MANAGER_EMAIL,
                FilterNamesEnum.USER_MANAGER_EMAIL,
              ].includes(filter.field) ? (
              <ManagersEmailMultiselectInput
                selectedOptions={value as Manager[]}
                onChange={(managers) => onChange(managers, filter.field)}
              />
            ) : [
                FilterNamesEnum.COUNTRY,
                FilterNamesEnum.USER_COUNTRY,
              ].includes(filter.field) ? (
              <CountriesMultiselectInput
                selectedOptions={value as Country[]}
                onChange={(countries) => onChange(countries, filter.field)}
              />
            ) : [FilterNamesEnum.JOB, FilterNamesEnum.USER_JOB].includes(
                filter.field,
              ) ? (
              <JobTitlesMultiselectInput
                selectedOptions={value as JobTitle[]}
                onChange={(jobTitles) => onChange(jobTitles, filter.field)}
              />
            ) : [
                FilterNamesEnum.COMPANY_NAME,
                FilterNamesEnum.USER_COMPANY_NAME,
              ].includes(filter.field) ? (
              <CompanyNamesMultiselectInput
                selectedOptions={value as CompanyName[]}
                onChange={(companyNames) =>
                  onChange(companyNames, filter.field)
                }
              />
            ) : [
                FilterNamesEnum.OFFICE_LOCATION,
                FilterNamesEnum.USER_OFFICE_LOCATION,
              ].includes(filter.field) ? (
              <OfficeLocationsMultiselectInput
                selectedOptions={value as OfficeLocation[]}
                onChange={(officeLocations) =>
                  onChange(officeLocations, filter.field)
                }
              />
            ) : [FilterNamesEnum.USER_BY_LANGUAGE].includes(filter.field) ? (
              <UserLanguagesMultiselectInput
                selectedOptions={value as AccountLanguage[]}
                onChange={(languages) => onChange(languages, filter.field)}
                placeholder={filter.placeholder as string}
              />
            ) : [FilterNamesEnum.COURSE_TAGS].includes(filter.field) ? (
              <TagsMultiselectInput
                selectedOptions={value as Tag[]}
                onChange={(tags) => onChange(tags, filter.field)}
              />
            ) : [
                FilterNamesEnum.COURSE_LANGUAGES,
                FilterNamesEnum.COURSE_SERIES,
              ].includes(filter.field) ? (
              <MultiSelect
                {...filter}
                placeholder={filter.placeholder as string}
                defaultValue={value as string[]}
                onChange={(event) => onChange(event.value, filter.field)}
                value={value}
                selectedItemsLabel={t('generic.itemsSelected.count')}
                {...preventDropdownBrowserTranslate}
                maxSelectedLabels={0}
              />
            ) : [FilterNamesEnum.EMAIL_TEMPLATE_BY_LANGUAGE].includes(
                filter.field,
              ) ? (
              <PhishingLanguagesMultiselectInput
                selectedOptions={
                  value as { id: string; value: string; name: string }[]
                }
                onChange={(languages) => onChange(languages, filter.field)}
              />
            ) : (
              <MultiSelect
                {...filter}
                display="chip"
                placeholder={filter.placeholder as string}
                defaultValue={value as string[]}
                onChange={(event) => onChange(event.value, filter.field)}
                value={value}
                selectedItemsLabel={t('generic.itemsSelected.count')}
                {...preventDropdownBrowserTranslate}
              />
            )}
          </>
        )}
        <AppButton
          className="group-button"
          icon="pi pi-times"
          severity="secondary"
          type="text"
          onClick={() => onRemove(filter.field)}
        />
      </div>
    </FlexContainer>
  );
};
